// Utils for working with the RUM client
import { AwsRum, AwsRumConfig } from 'aws-rum-web';
import { RUM_SESSION, RUM_USER } from '../constants/cookies';
import { AppConfig } from '../types/app';
import logger from '../utils/logger';
import { getShortbread } from './cookies';

let rumClient: AwsRum;
let initConfig: AppConfig;
const USER_ID_COOKIE_REGEX = new RegExp(`(^| )${RUM_USER}=([^;]+)`);

/**
 * Holds a single page-impression event recorded before the RUM client exists,
 * so it can be replayed once the client is created (see flushPendingImpression).
 *
 * Why only the impression: with the cookie component enabled, rumService.init()
 * does NOT create the client — it is created later by updateCookieConsent()
 * after the user resolves the consent banner. The page impression fires on
 * mount, before that, so under the old `if (rumClient)` guard it was dropped
 * entirely (the client did not exist, and the user then navigated to the IdP
 * before any batched dispatch could run). That under-counted the impression
 * denominator and produced impossible (>100%) page-conversion rates.
 *
 * This buffer is intentionally scoped to the impression ONLY. All other events
 * keep their original behavior (recorded if the client exists, otherwise
 * skipped) so no other metric is affected. A single slot is enough because an
 * impression fires once per page load; a later impression overwrites an earlier
 * un-flushed one rather than accumulating.
 */
let pendingImpression: { eventType: string; eventObject: object } | undefined;

const flushPendingImpression = () => {
  if (!rumClient || !pendingImpression) {
    return;
  }
  const { eventType, eventObject } = pendingImpression;
  pendingImpression = undefined;
  rumClient.recordEvent(eventType, eventObject);
};

export const areRUMCookiesAllowed = () => {
  const sb = getShortbread();
  return sb.hasConsent(RUM_USER) && sb.hasConsent(RUM_SESSION);
};

export const makeRumClient = (config: AppConfig, useCookies: boolean) => {
  if (!config?.rumConfig) {
    return;
  }
  const { appId, guestRoleArn, identityPoolId } = config.rumConfig;
  const rumConfig: AwsRumConfig = {
    identityPoolId,
    guestRoleArn,
    allowCookies: useCookies,
    enableXRay: true,
    sessionSampleRate: 1,
    sessionEventLimit: 0,
    endpoint: 'https://dataplane.rum.us-east-1.amazonaws.com',
    signing: true,
    telemetries: [
      'performance',
      'http',
      'errors',
      ['interaction', { events: [{ event: 'click', element: document }] }],
    ],
  };
  rumClient = new AwsRum(appId, '1.0.5', 'us-east-1', rumConfig);
  // If a page impression was recorded before the client existed, replay it now
  // so it is sent against a real session instead of being dropped.
  flushPendingImpression();
  return rumClient;
};

export const rumService = {
  init(appConfig: AppConfig) {
    initConfig = appConfig;
    if (Boolean(appConfig.enableCookieComponent === 'false')) {
      makeRumClient(initConfig, false);
    }
  },

  addSessionAttributes(attributes: {
    [key: string]: string | boolean | number;
  }) {
    logger.debug(`RUM addSessionAttributes: ${JSON.stringify(attributes)}`);
    if (rumClient) rumClient.addSessionAttributes(attributes);
  },

  recordError(e: Error | string) {
    logger.debug(`RUM recordError: ${e}`);
    if (rumClient) rumClient.recordError(e);
  },

  recordEvent(eventType: string, eventObject: object) {
    logger.debug(`RUM recordEvent: ${eventType}`);
    if (rumClient) rumClient.recordEvent(eventType, eventObject);
  },

  /**
   * Records the page-impression event. Unlike recordEvent, if the RUM client is
   * not yet initialized this buffers the impression and replays it once the
   * client is created, so the impression is not lost on early page loads. Use
   * this ONLY for the page impression — other events intentionally do not
   * buffer, to avoid changing their metrics.
   */
  recordImpression(eventType: string, eventObject: object) {
    logger.debug(`RUM recordImpression: ${eventType}`);
    if (rumClient) {
      rumClient.recordEvent(eventType, eventObject);
    } else {
      pendingImpression = { eventType, eventObject };
    }
  },

  dispatchBeacon() {
    if (rumClient) rumClient.dispatchBeacon();
  },

  updateCookieConsent() {
    makeRumClient(initConfig, areRUMCookiesAllowed());
  },

  getUserId(): string | undefined {
    let userId: string | undefined = undefined;
    if (typeof document !== 'undefined' && document.cookie) {
      const match = document.cookie.match(USER_ID_COOKIE_REGEX);
      userId = match ? match[2] : undefined;
    }
    logger.debug(`RUM UserID: ${userId}`);
    return userId;
  },
};
